import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, Sparkles, CheckCircle2, RotateCw, ShieldCheck, Target as TargetIcon, Flame, Clock, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { PRIORITY_LABELS, STATUS_LABELS } from "@/lib/i18n";
import { limitsFor } from "@/lib/plan";
import LockedCard from "@/components/LockedCard";
import ActionStateChip, { statusToActionState } from "@/components/yusi/ActionStateChip";
import ActionsFilterTabs from "@/components/yusi/ActionsFilterTabs";
import PreparedOutputDrawer from "@/components/yusi/PreparedOutputDrawer";

const PRIORITY_STYLES = {
  Kritik: "bg-red-500/15 text-red-300 border-red-500/30",
  Critical: "bg-red-500/15 text-red-300 border-red-500/30",
  Orta: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  Medium: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  Yüksek: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  High: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  Düşük: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  Low: "bg-slate-500/15 text-slate-300 border-slate-500/30",
};

const STATUS_STYLES = {
  Bekliyor: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  Pending: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  Uygulandı: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  Applied: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  "Yeniden Kontrol Edildi": "bg-purple-500/15 text-purple-300 border-purple-500/30",
  Rechecked: "bg-purple-500/15 text-purple-300 border-purple-500/30",
  Tamamlandı: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  Completed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
};

// Yusi Task Engine 2.0 — badge styles (dark-theme contrast tuned)
const HELP_LEVEL_STYLES = {
  A: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  B: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  C: "bg-amber-500/15 text-amber-300 border-amber-500/30",
};
const DIFFICULTY_STYLES = {
  easy:   "bg-emerald-500/10 text-emerald-200 border-emerald-500/20",
  medium: "bg-amber-500/10 text-amber-200 border-amber-500/20",
  hard:   "bg-slate-500/10 text-slate-300 border-slate-500/20",
};
const IMPACT_STYLES = {
  low:    "bg-slate-500/10 text-slate-300 border-slate-500/20",
  medium: "bg-cyan-500/10 text-cyan-200 border-cyan-500/20",
  high:   "bg-fuchsia-500/10 text-fuchsia-200 border-fuchsia-500/20",
};

// Content types Yusi can prepare on the task card. Must mirror
// CONTENT_TYPE_BRIEF in backend/server.py — kept in sync manually for now.
// Tasks with `content_type: "none"` or any value not in this set get no
// prepare button (the API would refuse anyway).
const PREPARABLE_CONTENT_TYPES = new Set([
  "google_description",
  "faq",
  "about",
  "service_page",
  "blog",
  "review_request",
  "instagram_bio",
  "meta_description",
  "localbusiness_schema",
  "faq_schema",
]);

// Backend translates priority/status to English when lang=en, but the canonical
// values in DB are still Turkish. These tiny helpers let the same filters
// work in both languages without forcing the user back to TR canonical keys.
const isCritical = (p) => p === "Kritik" || p === "Critical";
const isMediumOrLow = (p) => ["Orta", "Düşük", "Yüksek", "Medium", "Low", "High"].includes(p);
const isCompleted = (s) => s === "Tamamlandı" || s === "Completed";
const isPending = (s) => s === "Bekliyor" || s === "Pending";

export default function Tasks() {
  const { id } = useParams();
  const { user } = useAuth();
  const { lang, t } = useLang();
  const limits = limitsFor(user);
  const fullTasks = !!limits.full_tasks;
  const visibleCap = limits.tasks_visible;
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  // Yusi Actions — Phase 1: client-side filter state.
  const [filter, setFilter] = useState("all"); // all | detected | applied | completed

  const COLUMNS = [
    { key: "Kritik", title: t("tasks.col.critical"), icon: Flame, accent: "text-red-300", border: "border-red-500/20" },
    { key: "Orta", title: t("tasks.col.medium"), icon: Clock, accent: "text-amber-300", border: "border-amber-500/20" },
    { key: "Tamamlandı", title: t("tasks.col.completed"), icon: CheckCircle2, accent: "text-emerald-300", border: "border-emerald-500/20" },
  ];

  const load = () => {
    setLoading(true);
    api.get(`/tasks/${id}`, { params: { lang } }).then((r) => setTasks(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, [id, lang]);

  const markApplied = async (task) => {
    setBusyId(`apply-${task.id}`);
    try {
      await api.patch(`/tasks/${task.id}`, { status: "Uygulandı" });
      toast.success(t("tasks.applied.success"));
      load();
    } finally { setBusyId(null); }
  };

  const rescanTask = async (task) => {
    setBusyId(`rescan-${task.id}`);
    try {
      const { data } = await api.post("/rescan", { business_id: id, task_id: task.id });
      const v = data.verdict || {};
      if (v.implemented) toast.success(v.user_message || t("tasks.rescanApplied"));
      else toast.info(v.user_message || t("tasks.rescanNotDetected"));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("tasks.rescanError"));
    } finally { setBusyId(null); }
  };

  const manualConfirm = async (task) => {
    if (!window.confirm(t("tasks.manualConfirm"))) return;
    setBusyId(`manual-${task.id}`);
    try {
      const { data } = await api.post(`/tasks/${task.id}/manual-confirm`, {});
      toast.success(t("tasks.manualSuccess", { n: data.score_delta }));
      load();
    } catch (err) {
      toast.error(t("tasks.manualError"));
    } finally { setBusyId(null); }
  };

  // In-place updater used by the PreparedOutputDrawer so we don't refetch
  // the whole list every time the user prepares / edits / regenerates.
  const updateTaskInPlace = (updated) => {
    if (!updated?.id) return;
    setTasks((prev) => prev.map((tsk) => (tsk.id === updated.id ? { ...tsk, ...updated } : tsk)));
  };

  if (loading) return <div className="text-slate-400 flex items-center"><Loader2 className="animate-spin mr-2" /> {t("common.loading")}</div>;

  // Yusi Actions filter counts — count BEFORE applying the filter.
  const actionCounts = tasks.reduce(
    (acc, task) => {
      acc.all += 1;
      const st = statusToActionState(task.status);
      if (st === "detected") acc.detected += 1;
      else if (st === "applied" || st === "rechecked") acc.applied += 1;
      else if (st === "completed") acc.completed += 1;
      return acc;
    },
    { all: 0, detected: 0, applied: 0, completed: 0 }
  );

  // Apply the chosen filter to the task list that drives the columns below.
  // "Rechecked" tasks fold into the "Applied" tab (Phase 1 spec).
  const filterMatches = (task) => {
    if (filter === "all") return true;
    const st = statusToActionState(task.status);
    if (filter === "applied") return st === "applied" || st === "rechecked";
    return st === filter;
  };
  const filteredTasks = tasks.filter(filterMatches);

  // Free plan: show only the first `visibleCap` non-completed tasks; lock the rest.
  const pendingTasks = filteredTasks.filter((task) => !isCompleted(task.status));
  const visiblePending = !fullTasks && visibleCap > 0 ? pendingTasks.slice(0, visibleCap) : pendingTasks;
  const hiddenCount = pendingTasks.length - visiblePending.length;
  const visibleIds = new Set(visiblePending.map((task) => task.id));

  const grouped = {
    Kritik: visiblePending.filter((task) => isCritical(task.priority)),
    Orta: visiblePending.filter((task) => isMediumOrLow(task.priority)),
    Tamamlandı: filteredTasks.filter((task) => isCompleted(task.status)),
  };
  if (!fullTasks && visibleCap > 0 && grouped.Kritik.length === 0 && grouped.Orta.length === 0 && visiblePending.length > 0) {
    grouped.Kritik = visiblePending;
  }
  void visibleIds;

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div>
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-cyan-400 mb-2"><TargetIcon size={12} /> {t("tasks.eyebrow")}</div>
        <h1 className="font-display text-4xl font-bold text-white">{t("tasks.title")}</h1>
        <p className="text-slate-300 mt-2 max-w-3xl leading-relaxed">{t("tasks.subtitle")}</p>
        <p className="text-xs text-slate-500 mt-3 max-w-3xl leading-relaxed">{t("tasks.legacyHelp")}</p>
      </div>

      {tasks.length > 0 && (
        <ActionsFilterTabs value={filter} onChange={setFilter} counts={actionCounts} />
      )}

      {/* Phase 4 — small link to the Yusi Permissions panel in Settings. */}
      {tasks.length > 0 && (
        <Link
          to="/app/ayarlar#yusi-permissions"
          className="flex items-center justify-between gap-3 rounded-xl border border-cyan-500/20 bg-cyan-500/[0.05] hover:bg-cyan-500/[0.08] px-4 py-3 transition-colors"
          data-testid="tasks-perm-link"
        >
          <span className="inline-flex items-center gap-2 text-[12.5px] text-cyan-100">
            <Lock size={14} strokeWidth={2} className="text-cyan-300 shrink-0" />
            <span>{t("tasks.permLink.text")}</span>
          </span>
          <span className="text-[11px] text-cyan-300 font-medium shrink-0">
            {t("tasks.permLink.cta")} →
          </span>
        </Link>
      )}

      {tasks.length === 0 && (
        <div className="glass-strong rounded-2xl p-10 text-center text-slate-400">
          {t("tasks.empty")}{" "}
          <Link to={`/app/isletme/${id}/analiz`} className="text-cyan-300 hover:text-cyan-200">{t("tasks.runAnalysisLink")}</Link>.
        </div>
      )}

      {!fullTasks && hiddenCount > 0 && (
        <LockedCard
          title={t("tasks.locked.title", { n: hiddenCount })}
          description={t("tasks.locked.desc")}
          requiredPlan="Starter"
          testid="tasks-locked-card"
        />
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {COLUMNS.map((col) => (
          <div key={col.key} className={`rounded-3xl glass-strong border ${col.border} p-5 min-h-[300px]`} data-testid={`tasks-col-${col.key}`}>
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/5">
              <div className="flex items-center gap-2">
                <col.icon size={16} className={col.accent} strokeWidth={1.7} />
                <h2 className="font-display font-semibold text-white">{col.title}</h2>
              </div>
              <span className="font-mono text-xs text-slate-400">{grouped[col.key].length}</span>
            </div>
            <div className="space-y-3">
              {grouped[col.key].length === 0 && (
                <div className="text-sm text-slate-600 py-6 text-center">
                  {col.key === "Tamamlandı" ? t("tasks.emptyCol.completed") : t("tasks.emptyCol.default")}
                </div>
              )}
              {grouped[col.key].map((task) => (
                <div key={task.id} className="rounded-xl bg-white/[0.02] border border-white/5 p-4 hover:border-white/15 transition-all" data-testid={`task-${task.id}`}>
                  {task.main_category && (
                    <div className="text-[10px] uppercase tracking-wider text-cyan-300/90 mb-1.5 truncate" data-testid={`task-${task.id}-main-cat`}>
                      {t(`tasks.mainCat.${task.main_category}`)}
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-1.5 mb-2">
                    <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${PRIORITY_STYLES[task.priority] || ""}`}>
                      {PRIORITY_LABELS[lang]?.[task.priority] || task.priority}
                    </span>
                    <span className="text-[10px] text-cyan-300 font-mono">+{task.impact_points}</span>
                    <ActionStateChip status={task.status} />
                    {task.manual_confirmed && <span className="text-[10px] text-purple-300">{t("tasks.manualBadge")}</span>}
                  </div>
                  <h3 className="font-display text-sm font-semibold text-white mb-1.5 leading-tight">{task.title}</h3>
                  <div className="text-[11px] text-slate-500 mb-2">{task.category}</div>
                  <p className="text-xs text-slate-400 mb-2 leading-relaxed line-clamp-3">{task.why}</p>

                  {(task.difficulty || task.estimated_impact) && (
                    <div className="flex flex-wrap items-center gap-1.5 mb-2" data-testid={`task-${task.id}-meta-chips`}>
                      {task.difficulty && (
                        <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${DIFFICULTY_STYLES[task.difficulty] || ""}`}>
                          {t(`tasks.difficulty.${task.difficulty}`)}
                        </span>
                      )}
                      {task.estimated_impact && (
                        <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${IMPACT_STYLES[task.estimated_impact] || ""}`}>
                          {t(`tasks.impact.${task.estimated_impact}`)}
                        </span>
                      )}
                    </div>
                  )}

                  {task.yusi_help_level && (
                    <div className="mb-3 rounded-lg border border-white/5 bg-white/[0.015] p-2.5" data-testid={`task-${task.id}-yusi-help`}>
                      <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">
                        {t("tasks.helpLevel.sectionTitle")}
                      </div>
                      <div className="flex items-start gap-2">
                        <span className={`shrink-0 text-[9px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${HELP_LEVEL_STYLES[task.yusi_help_level] || ""}`}>
                          {t(`tasks.helpLevel.${task.yusi_help_level}.label`)}
                        </span>
                        <p className="text-[11px] text-slate-400 leading-relaxed">
                          {t(`tasks.helpLevel.${task.yusi_help_level}.desc`)}
                        </p>
                      </div>
                    </div>
                  )}

                  <p className="text-[11px] text-cyan-200/80 italic mb-3 leading-relaxed" data-testid={`task-${task.id}-microcopy`}>
                    {t("tasks.cardMicrocopy")}
                  </p>

                  {task.rescan_verdict?.user_message && (
                    <div className={`mb-3 rounded-lg border p-2.5 text-[11px] ${
                      task.rescan_verdict.implemented
                        ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-100"
                        : "bg-amber-500/10 border-amber-500/20 text-amber-100"
                    }`}>
                      {task.rescan_verdict.user_message}
                    </div>
                  )}

                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_STYLES[task.status] || ""}`}>
                      {STATUS_LABELS[lang]?.[task.status] || task.status}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-1.5">
                    {task.content_type && task.content_type !== "none" && PREPARABLE_CONTENT_TYPES.has(task.content_type) && (
                      <Link
                        to={`/app/isletme/${id}/icerik?type=${task.content_type}&task=${task.id}`}
                        className="ghost-btn rounded-md px-2.5 py-1.5 text-[10px] text-white inline-flex items-center gap-1"
                        data-testid={`task-${task.id}-content`}
                      >
                        <Sparkles size={10} /> {t("tasks.contentBtn")}
                      </Link>
                    )}
                    {/* Yusi Task Engine 2.0 — placeholder for A-level tasks that don't yet
                        have a real preparer wired up. Purely visual, no action. */}
                    {task.yusi_help_level === "A"
                      && !PREPARABLE_CONTENT_TYPES.has(task.content_type)
                      && fullTasks
                      && !isCompleted(task.status) && (
                      <button
                        type="button"
                        disabled
                        aria-disabled="true"
                        title={t("tasks.prepareBtnComingSoon")}
                        className="rounded-md px-2.5 py-1.5 text-[10px] text-emerald-200/70 border border-emerald-500/20 bg-emerald-500/5 inline-flex items-center gap-1 cursor-not-allowed opacity-70"
                        data-testid={`task-${task.id}-prepare-coming-soon`}
                      >
                        <Sparkles size={10} /> {t("tasks.prepareBtnComingSoon")}
                      </button>
                    )}
                    {isPending(task.status) && (
                      <button
                        onClick={() => markApplied(task)}
                        disabled={busyId === `apply-${task.id}`}
                        className="ghost-btn rounded-md px-2.5 py-1.5 text-[10px] text-white inline-flex items-center gap-1"
                        data-testid={`task-${task.id}-mark-applied`}
                      >
                        {busyId === `apply-${task.id}` ? <Loader2 size={10} className="animate-spin" /> : <CheckCircle2 size={10} />} {t("tasks.appliedBtn")}
                      </button>
                    )}
                    {!isCompleted(task.status) && limits.monthly_rescan > 0 && (
                      <button
                        onClick={() => rescanTask(task)}
                        disabled={busyId === `rescan-${task.id}`}
                        className="neon-btn rounded-md px-2.5 py-1.5 text-[10px] text-white inline-flex items-center gap-1 disabled:opacity-60"
                        data-testid={`task-${task.id}-rescan`}
                      >
                        {busyId === `rescan-${task.id}` ? <Loader2 size={10} className="animate-spin" /> : <RotateCw size={10} />} {t("tasks.rescanBtn")}
                      </button>
                    )}
                    {!isCompleted(task.status) && task.rescan_verdict && !task.rescan_verdict.implemented && (
                      <button
                        onClick={() => manualConfirm(task)}
                        disabled={busyId === `manual-${task.id}`}
                        className="rounded-md px-2.5 py-1.5 text-[10px] text-purple-200 border border-purple-500/30 bg-purple-500/10 hover:bg-purple-500/20 inline-flex items-center gap-1"
                        data-testid={`task-${task.id}-manual-confirm`}
                      >
                        {busyId === `manual-${task.id}` ? <Loader2 size={10} className="animate-spin" /> : <ShieldCheck size={10} />} {t("tasks.manualBtn")}
                      </button>
                    )}
                  </div>

                  {PREPARABLE_CONTENT_TYPES.has(task.content_type) && (
                    <PreparedOutputDrawer task={task} onUpdated={updateTaskInPlace} />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
